import axios, { AxiosInstance } from "axios";
import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useGlobal } from "./global";
import { modules } from "../utils/modules";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setUpConsultation } from "../services/generalServices";
import {
  getFranchiseLabel,
} from "../utils/labels";

import {
  businessProps,
  cityProps,
  notificationsAttributes,
  stateProps,
  userProps,
} from "../interfaces";
import { IPermissionsTypes } from "../utils/permissions";
import { addDays } from "date-fns";

interface UserInterface extends userProps { }

interface DataProps {
  token: string;
  user: UserInterface;
}

export interface credentialsProps {
  email: string;
  password: string;
}

interface ISearchProps {
  search: string;
  limit?: number;
}

interface ISearchCitiesProps extends ISearchProps {
  state_id?: string;
}

interface ISearchDocumentsTypesProps extends ISearchProps {
  type: "PF" | "PJ" | "property";
}

interface ISearchSignatoriesProps {
  property_id?: string;
  proposal_id?: string;
}

interface IConsultsServicesProps {
  business_id: (props: ISearchProps) => Promise<{ data: any; formatted: any }>;
  state_id: (props: ISearchProps) => Promise<{ data: any; formatted: any }>;
  city_id: (
    props: ISearchCitiesProps
  ) => Promise<{ data: any; formatted: any }>;
  address: (
    props: ISearchProps
  ) => Promise<{ address?: any; city?: cityProps; state?: stateProps }>;
  document_type_id: (
    props: ISearchDocumentsTypesProps
  ) => Promise<{ data: any; formatted: any }>;
  signatories: (props: ISearchSignatoriesProps) => Promise<any>;
  contract_model_id: (
    props: ISearchProps
  ) => Promise<{ data: any; formatted: any }>;
  user_type_id: (props: ISearchProps) => Promise<{ data: any; formatted: any }>;
  default_id: (props: ISearchProps) => Promise<{ data: any; formatted: any }>;
  [key: string]: (
    props: any
  ) => Promise<
    | { address?: any; city?: cityProps; state?: stateProps }
    | { data: any; formatted: any }
  >;
}

interface ApiContextData {
  consultsServices: IConsultsServicesProps;
  api: AxiosInstance;
  user: UserInterface;
  token?: string;
  signIn(credentials: credentialsProps, remmemberMe?: boolean): Promise<void>;
  signOut(): void;
  notifications: notificationsAttributes[];
  setNotifications: React.Dispatch<
    React.SetStateAction<notificationsAttributes[]>
  >;
  setIsForceSearchAll: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  updateUser(): Promise<void>;
  setUser(user: any): Promise<void>;
  remmemberMe: boolean;
  isForceSearchAll: boolean;
  markAsRead: (data: any) => Promise<void>;
  setRemmemberMe: React.Dispatch<React.SetStateAction<boolean>>;
  remmemberCredentials: credentialsProps;
  businessList: businessProps[];
}

const ApiContext = createContext<ApiContextData>({} as ApiContextData);

export const ApiProvider: React.FC = ({ children }) => {
  const { notify, notifyOnly, changeBusinessGlobal } = useGlobal();

  const [notifications, setNotifications] = useState<notificationsAttributes[]>(
    []
  );
  const [remmemberMe, setRemmemberMe] = useState(() => {
    const rem = localStorage.getItem("@web-checkin:remmember");
    return !!rem;
  });
  const [remmemberCredentials, setRemmemberCredentials] = useState(() => {
    const rem = localStorage.getItem("@web-checkin:remmember");
    return rem ? { ...JSON.parse(rem) } : {};
  });
  const [data, setData] = useState<DataProps>(() => {
    const token = localStorage.getItem("@web-checkin:token");
    const user = localStorage.getItem("@web-checkin:user");
    if (token && user) return { token, user: JSON.parse(user) };
    return {} as DataProps;
  });
  const navigate = useNavigate();
  const [redirectDashboard, setRedirectDashboard] = useState(false);
  const [redirectSubscription, setRedirectSubscription] = useState(false);
  const [isForceSearchAll, setIsForceSearchAll] = useState(false);
  const [businessList, setBusinessList] = useState<businessProps[]>([]);

  const api = axios.create({ baseURL: process.env.REACT_APP_API });

  const [params] = useSearchParams();

  api.interceptors.request.use(async (config: any) => {
    const token = localStorage.getItem("@web-checkin:token");
    const bus_id = params.get("bus_id") || localStorage.getItem("@web-checkin:bus_id");
    const url = config.url;
    const query = url.split("?")[1];

    if (!url.includes("bus_id") /* && !data?.user?.is_root */) {
      if (query) {
        if (url?.split("")?.pop() === "&")
          config.url = `${url}bus_id=${bus_id}`;
        else config.url = `${url}&bus_id=${bus_id}`;
      } else if (url.includes("?"))
        config.url = `${url}bus_id=${bus_id}`;
      else config.url = `${url}?bus_id=${bus_id}`;
    }

    config.headers.authorization = `Bearer ${token}`;

    return config;
  });

  api.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      if (error.response.status === 401) {
        if (error.response.data.path === "plan-not-have-permition") {
          const entity = error.response.data.entity;
          const module = modules.find((m) => m.name === entity)?.description;
          notifyOnly(
            `O plano de assinatura da sua empresa não permite acesso ao módulo "${module}"`,
            "alert"
          );
          navigate("/subscription");
        } else {
          notifyOnly("Usuário sem permissão para realizar a ação", "error");
          // signOut();
        }
      }
      if (error.response.status === 402) {
        const correct_situations: any = {
          "not-have-subscription":
            "Empresa ainda não possui uma assinatura!",
          "not-have-transactions":
            "Assinatura da empresa ainda não possui cobranças geradas!",
          "subscription-canceled": "Assinatura da empresa está cancelada!",
          "subscription-expired": "Assinatura da empresa está expirada!",
          "subscription-not-paid":
            "Assinatura da empresa está suspensa por falta de pagamento!",
          "subscription-waiting-block":
            "Assinatura da empresa está aguardando pagamento para a ativação!",
        };

        if (correct_situations[error.response.data.path]) {
          notifyOnly(correct_situations[error.response.data.path], "alert");
        } else {
          notifyOnly(
            "Algum problema relacionado a assinatura da empresa! Entre em contato conosco!",
            "alert"
          );
        }

        navigate("/subscription");
      }
      return Promise.reject(error);
    }
  );

  const searchBusiness = async (props: ISearchProps) => {
    const string = setUpConsultation(props);

    try {
      const result = await api.get(`/business?${string}`);
      const formatted = result?.data?.data?.map((item: any) => ({
        label: getFranchiseLabel({ business: item }),
        value: item.id,
        this: item,
      }));

      const data = result.data;
      return { data, formatted };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  };

  const searchState = async (props: ISearchProps) => {
    const string = setUpConsultation(props);

    try {
      const result = await api.get(`/states?${string}`);
      const formatted = result.data.rows.map((item: any) => ({
        label: item.name,
        value: item.id,
        initials: item.initials,
        this: item,
      }));

      const data = result.data.rows;
      return { data, formatted };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  };

  const searchCities = async (props: ISearchCitiesProps) => {
    const string = setUpConsultation(props);

    try {
      if (!props.state_id) return { data: [], formatted: [] };
      const result = await api.get(`/cities?${string}`);
      const formatted = result.data.rows.map((item: any) => ({
        label: item.name,
        value: item.id,
        this: item,
      }));

      const data = result.data.rows;
      return { data, formatted };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  };

  const searchAddress = useCallback(async (props: ISearchProps) => {
    const { search } = props;

    try {
      const result = await api.get(`/address/${search}`);

      const address = result.data.address;

      const city: cityProps = result.data.city;

      const state: stateProps = result.data.state;

      return { address, city, state };
    } catch (err) {
      return {};
    }
  }, []);

  const searchDocumentsTypes = useCallback(
    async (props: ISearchDocumentsTypesProps) => {
      const string = setUpConsultation({ ...props, limit: 50 });

      try {
        const result = await api.get(
          `/generic_searches/documents-types?${string}`
        );

        const formatted = result.data.rows.map((item: any) => {
          return { label: item.name, value: item.id, this: item };
        });

        const data = result.data.rows;

        return { data, formatted };
      } catch (err) {
        return { data: [], formatted: [] };
      }
    },
    []
  );

  const searchSignatories = useCallback(
    async (props: ISearchSignatoriesProps) => {
      const { proposal_id, property_id } = props;

      try {
        const _type = proposal_id ? "proposals" : "properties";

        const _id = proposal_id ? proposal_id : property_id;

        const url = `/${_type}/signatories/${_id}`;

        const { data } = await api.get(url);

        return data;
      } catch (error) {
        return [];
      }
    },
    []
  );

  const searchContractModels = useCallback(async (props: ISearchProps) => {
    const string = setUpConsultation({ ...props, limit: 50 });

    try {
      const result = await api.get(
        `/generic_searches/contract-models?${string}`
      );

      const formatted = result.data.rows.map((item: any) => {
        return { label: item.name, value: item.id, this: item };
      });

      const data = result.data.rows;

      return { data, formatted };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  }, []);

  const searchUserTypes = useCallback(async ({ search }: ISearchProps) => {
    try {
      const result = await api.get("/user-types", { params: { search } });

      const formatted = result.data.data.map((item: any) => {
        return { label: item.name, value: item.id, this: item };
      });

      const data = result.data.data;

      return { data, formatted };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  }, []);

  const searchAccommodation = useCallback(async ({ search }: ISearchProps) => {
    return { data: [], formatted: [] };
  }, []);

  const searchReservations = useCallback(async ({ search }: ISearchProps) => {
    try {
      const result = await api.get("/check-ins/availables", {
        params: { search },
      });

      const formatted = result.data.data.map((item: any) => {
        return {
          label: item.reservation_id,
          value: item.reservation_id,
          this: item,
        };
      });

      const data = result.data.data;

      return { data, formatted };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  }, []);

  const consultsServices: IConsultsServicesProps = {
    business_id: searchBusiness,
    state_id: searchState,
    city_id: searchCities,
    address: searchAddress,
    document_type_id: searchDocumentsTypes,
    signatories: searchSignatories,
    contract_model_id: searchContractModels,
    user_type_id: searchUserTypes,
    stays_id: searchAccommodation,
    reservation_stays_id: searchReservations,
    default_id: async () => {
      return { data: [], formatted: [] };
    },
  };

  const searchNotifications = useCallback(async () => {
    try {
      const result = await api.get("/notifications");
      setNotifications([...result.data]);
    } catch (err) {
      notify("Erro ao buscar notificações", "error");
      console.error(err);
    }
  }, []);

  const markAsRead = useCallback(
    async ({ user_id, property_id, proposal_id, contract_id, type }) => {
      await api.post("/notifications/maskAsRead", {
        user_id,
        property_id,
        proposal_id,
        contract_id,
        type,
      });
      await searchNotifications();
    },
    []
  );

  const addDays = (date: any, days: number) => {
    const copy = new Date(date);
    copy.setDate(date.getDate() + days);
    return copy;
  };

  const signIn = useCallback(
    async ({ email, password }, remmemberMe = false) => {
      const res = await api.post("/session", { email, password });

      const { token, user } = res.data;

      if (user.user_type_id || user?.is_root) {

        user.integration_stays_active = user.integration_stays_active ?? true;

        if (remmemberMe) {
          localStorage.setItem(
            "@web-checkin:remmember",
            JSON.stringify({ email, password })
          );
          setRemmemberCredentials({ email, password });
        } else {
          localStorage.removeItem("@web-checkin:remmember");
          setRemmemberCredentials({ email: "", password: "" });
        }

        localStorage.setItem("@web-checkin:token", token);
        localStorage.setItem("@web-checkin:bus_id", user.bus_id);
        localStorage.setItem("@web-checkin:user", JSON.stringify(user));
        setData({ token, user });

        if (user?.business) {
          changeBusinessGlobal(user?.business)
        } else {
          changeBusinessGlobal({} as businessProps)
        }

        const permissionDashboard = user?.user_type?.permissions?.filter((permission: IPermissionsTypes) => permission === 'DASHBOARD');
        const permissionBusinessDashboard = user?.business?.subscriptions?.plans?.permissions?.filter((permission: IPermissionsTypes) => permission === 'DASHBOARD');

        if (permissionDashboard?.length > 0 && permissionBusinessDashboard?.length > 0 && user?.integration_stays_active) {
          let active = isActiveNormallySystem();

          setRedirectDashboard(active!);
        }

      } else notify("Você não tem permissão para acessar!", "alert");
    },
    []
  );

  const isActiveNormallySystem = useCallback(() => {
    let active = true;

    const userString: any = localStorage.getItem("@web-checkin:user");
    const user = userString ? JSON.parse(userString) : null;

    if (user?.integration_stays_active) {
      active = true
    } else {
      active = false
    }

    if (user?.business?.business_data?.is_free_test) {
      const expireDate = new Date(user?.business?.business_data?.expire_free_test ?? '');
      const now = new Date();

      expireDate.setHours(0, 0, 0, 0);
      now.setHours(0, 0, 0, 0);

      if (expireDate < now) {
        active = false
      }
    }

    //verificação para boleto
    if (user?.business?.subscriptions?.transactions?.[0]?.method === 'banking_billet') {
      if (user?.business?.subscriptions?.transactions?.[0]?.createdAt) {
        const expireDate = addDays(new Date(
          user?.business.subscriptions?.transactions?.[0]?.createdAt as any ??
          user?.business?.business_data?.expire_free_test
        ),
          user?.business.subscriptions?.transactions?.[0]?.createdAt ? 7 : 0
        );

        const now = new Date();

        expireDate.setHours(0, 0, 0, 0);
        now.setHours(0, 0, 0, 0);

        if (expireDate < now) {
          active = false
        }
      }
    }

    //verificação para cartão de crédito
    if (user?.business?.subscriptions?.transactions?.[0]?.method === 'credit_card') {
      if (user?.business?.subscriptions?.next_expire_at && user?.business?.subscriptions?.transactions?.[0]?.status !== 'paid') {
        const expireDate = new Date(user?.business?.subscriptions?.next_expire_at ?? '');
        const now = new Date();

        expireDate.setHours(0, 0, 0, 0);
        now.setHours(0, 0, 0, 0);

        if (expireDate < now) {
          active = false
        }
      }
    } 

    if (['canceled', 'expired'].includes(user?.business?.subscriptions?.status)) {
      active = false
    }

    if (!user?.integration_stays_active) {
      active = false
    }

    if (user?.business?.trust) {
      active = true
    }

    if (!user?.integration_stays_active) {
      active = false
    }

    if (JSON.parse(userString)?.is_root) {
      active = true
    }

    return active;
  }, [localStorage.getItem("@web-checkin:user")])

  const signOut = useCallback(() => {
    localStorage.removeItem("@web-checkin:token");
    localStorage.removeItem("@web-checkin:user");
    localStorage.removeItem("@web-checkin:bus_id");
    localStorage.removeItem("@web-checkin:business");
    localStorage.removeItem("@web-checkin:business");
    setData({} as DataProps);

  }, []);

  const setUser = useCallback(async (new_user) => {
    try {
      localStorage.setItem("@web-checkin:user", JSON.stringify(new_user));
      setData((atualData) => {
        return { token: atualData.token, user: new_user };
      });

    } catch (err) {
      console.error(err);
    }
  }, []);

  const updateUser = useCallback(async () => {
    try {
      const result = await api.get("/users/my-infos");
      const new_user = result.data;
      localStorage.setItem("@web-checkin:user", JSON.stringify(new_user));
      setData((atualData) => {
        return { token: atualData.token, user: new_user };
      });
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const bus_id = params.get("bus_id");
    if (bus_id) {
      localStorage.setItem("@web-checkin:bus_id", bus_id);
    }
  }, [params])

  useEffect(() => {
    if (redirectDashboard) {
      navigate("/dashboard");

      setRedirectDashboard(false);
    }
  }, [redirectDashboard])

  useEffect(() => {
    if (redirectSubscription) {
      navigate("/subscription");

      setRedirectSubscription(false);
    }
  }, [redirectSubscription])

  useEffect(() => {
    if (data?.user?.is_root && !businessList?.length) {
      const searchBusiness = async () => {
        const result = await consultsServices.business_id({ search: '', limit: 1000 });
        setBusinessList(result?.data?.data || []);
      }

      searchBusiness();
    }
  }, [data?.user])

  return (
    <ApiContext.Provider
      value={{
        signIn,
        markAsRead,
        remmemberCredentials,
        remmemberMe,
        setRemmemberMe,
        updateUser,
        setUser,
        notifications,
        setNotifications,
        signOut,
        user: data.user,
        token: data.token,
        api,
        consultsServices,
        isForceSearchAll,
        setIsForceSearchAll,
        businessList
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export function useApi(): ApiContextData {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return context;
}
